import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, fmtMoney, fmtNum, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileText, Trash2, Calculator, CheckCircle2, AlertTriangle, XCircle, Download, Play, Ruler, BarChart3 } from "lucide-react";
import DxfViewer from "@/components/DxfViewer";
import CadJobPanel from "@/components/CadJobPanel";
import PdfAnalysisPanel from "@/components/PdfAnalysisPanel";
import OperationalRFQ from "@/pages/OperationalRFQ";

const LEGACY_LABS = process.env.REACT_APP_ENABLE_LEGACY_LABS === "true";

function Reliability({ level, reasons = [] }) {
  const cfg = {
    green: { icon: CheckCircle2, label: "VERDE — Preventivo affidabile" },
    yellow: { icon: AlertTriangle, label: "GIALLO — Revisione consigliata" },
    red: { icon: XCircle, label: "ROSSO — Dati critici mancanti" },
  }[level || "red"];
  const Icon = cfg.icon;
  return (
    <div className={`border p-4 flex items-start gap-3 bg-reliability-${level || "red"}`} data-testid={`reliability-${level || "red"}`}>
      <Icon size={22} className="mt-0.5" />
      <div className="flex-1">
        <div className="font-display font-bold text-sm">{cfg.label}</div>
        {reasons.length > 0 && (
          <ul className="mt-1 text-xs space-y-0.5 list-disc pl-4">
            {reasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function RFQDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdminOrOwner = ["owner", "admin"].includes(user?.role);
  const [rfq, setRfq] = useState(null);
  const [config, setConfig] = useState({ materials: [], machines: [], bending: {}, overhead: {} });
  const [inputs, setInputs] = useState({});
  const [quantities, setQuantities] = useState("1,10,50");
  const [calc, setCalc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);
  const [activePdfJobId, setActivePdfJobId] = useState(null);
  const fileInput = useRef();
  const navigate = useNavigate();

  const loadAll = async () => {
    const [{ data: r }, { data: c }] = await Promise.all([api.get(`/rfqs/${id}`), api.get("/config")]);
    setRfq(r); setConfig(c);
    setInputs(r.manual_inputs || {});
    if (r.quantities?.length) setQuantities(r.quantities.join(","));
    // auto-open the most recent job if none active
    if (!activeJobId) {
      const jobFile = (r.files || []).find(f => f.cad_job_id);
      if (jobFile) setActiveJobId(jobFile.cad_job_id);
    }
  };
  useEffect(() => { loadAll(); }, [id]);

  const analyzeFile = async (fid) => {
    try {
      const { data } = await api.post(`/files/${fid}/analyze`, { provider: "opensource" });
      toast.success(`Analisi DXF avviata (stato: ${data.state})`);
      setActiveJobId(data.job_id);
      loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const analyzePdf = async (fid) => {
    try {
      const { data } = await api.post(`/pdf-jobs/${fid}/analyze`, { use_vision: false });
      toast.success(`Analisi PDF completata (${data.result?.document?.classification || "?"})`);
      setActivePdfJobId(data.job_id);
      loadAll();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "object" ? (d?.detail || d?.code || "Errore PDF") : formatApiErrorDetail(d));
    }
  };

  if (!rfq) return <div className="p-8 text-muted-foreground text-sm">Caricamento…</div>;
  if (rfq.operational_flow) return <OperationalRFQ rfq={rfq} onChanged={loadAll} />;

  const upload = async (files) => {
    const uploaded = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post(`/rfqs/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`Caricato: ${file.name}`);
        uploaded.push(data);
        // v1.2 auto-analysis: if backend auto-triggered analysis, the
        // upload response already carries pdf_job_id — open the panel.
        if (data?.pdf_job_id) {
          setActivePdfJobId(data.pdf_job_id);
          toast.info(`Analisi PDF avviata automaticamente — apertura pannello...`, { duration: 2500 });
        }
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    }
    await loadAll();
    // Fallback: for PDFs whose auto-analysis failed (or if the backend
    // didn't ship the pdf_job_id in the response for any reason), try
    // an explicit analyze call once. Silent on already-analyzed.
    for (const f of uploaded) {
      if (f?.extension === ".pdf" && !f?.pdf_job_id) {
        try {
          const { data } = await api.post(`/pdf-jobs/${f.id}/analyze`, { use_vision: false });
          setActivePdfJobId(data.job_id);
        } catch { /* silent — user can manually retry */ }
      }
    }
  };
  const deleteFile = async (fid) => {
    if (!window.confirm("Eliminare file?")) return;
    await api.delete(`/files/${fid}`);
    toast.success("File eliminato");
    loadAll();
  };
  const setI = (k, v) => setInputs({ ...inputs, [k]: v });

  const calculate = async () => {
    const q = quantities.split(",").map(x => parseInt(x.trim())).filter(x => x > 0);
    try {
      const { data } = await api.post("/pricing/calculate", { inputs, quantities: q });
      setCalc(data);
      toast.success(`Calcolo eseguito — affidabilità ${data.reliability?.toUpperCase()}`);
      // save inputs on RFQ
      await api.put(`/rfqs/${id}`, {
        manual_inputs: inputs, quantities: q,
        reliability: data.reliability, warnings: data.warnings,
        status: data.reliability === "green" ? "ready_to_quote" : "needs_review",
      });
      loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const createQuote = async () => {
    setSaving(true);
    try {
      const q = quantities.split(",").map(x => parseInt(x.trim())).filter(x => x > 0);
      const { data } = await api.post("/quotes", { rfq_id: id, inputs, quantities: q });
      toast.success(`Preventivo ${data.number} creato`);
      navigate(`/quotes/${data.id}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-8" data-testid="rfq-detail">
      <div className="mb-6">
        <Link to="/rfqs" className="text-xs text-muted-foreground hover:text-foreground">← Richieste</Link>
        <div className="flex items-end justify-between mt-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">{rfq.customer?.name || "—"}</div>
            <h1 className="text-3xl font-display font-bold mt-1 font-mono">{rfq.reference}</h1>
            <div className="text-sm text-muted-foreground mt-1">{rfq.description}</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 border text-[10px] uppercase tracking-widest">{rfq.status}</span>
            <span className={`px-2 py-1 border text-[10px] uppercase tracking-widest bg-reliability-${rfq.reliability || "red"}`}>{rfq.reliability || "red"}</span>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* LEFT: Files + Manual inputs */}
        <div className="space-y-6">
          <section className="border border-border bg-white">
            <div className="px-4 py-3 border-b border-border font-display font-bold text-sm">File caricati</div>
            <div className="p-4">
              <div
                className="border-2 border-dashed border-border p-8 text-center cursor-pointer hover:bg-muted transition-colors"
                onClick={() => fileInput.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); upload(Array.from(e.dataTransfer.files)); }}
                data-testid="upload-dropzone"
              >
                <Upload size={28} className="mx-auto text-muted-foreground" />
                <div className="mt-2 text-sm">Trascina qui DXF, STEP o PDF</div>
                <div className="text-xs text-muted-foreground mt-1">o clicca per selezionare · max 50MB</div>
                <input ref={fileInput} type="file" hidden multiple accept=".dxf,.step,.stp,.pdf"
                  onChange={(e) => upload(Array.from(e.target.files))} data-testid="upload-input" />
              </div>
              <div className="mt-4 space-y-1">
                {(rfq.files || []).map(f => (
                  <div key={f.id} className="border border-border" data-testid={`file-row-${f.id}`}>
                    <div className="flex items-center justify-between px-3 py-2 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText size={14} className="text-muted-foreground shrink-0" />
                        <span className="truncate font-mono text-xs">{f.original_name}</span>
                        <span className="text-[10px] text-muted-foreground uppercase">{f.extension}</span>
                        <span className="text-[10px] text-muted-foreground">{(f.size / 1024).toFixed(1)} KB</span>
                        {f.cad_job_id && (
                          <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-border bg-muted">
                            {f.analysis_status}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {f.extension === ".dxf" && !f.cad_job_id && (
                          <button onClick={() => analyzeFile(f.id)} className="text-xs border border-border px-2 py-0.5 hover:bg-muted flex items-center gap-1" data-testid={`analyze-${f.id}`}>
                            <Play size={10} />Analizza DXF
                          </button>
                        )}
                        {f.extension === ".pdf" && !f.pdf_job_id && (
                          <button onClick={() => analyzePdf(f.id)} className="text-xs border border-border px-2 py-0.5 hover:bg-muted flex items-center gap-1" data-testid={`analyze-pdf-${f.id}`}>
                            <Play size={10} />Analizza PDF
                          </button>
                        )}
                        {f.cad_job_id && (
                          <button onClick={() => setActiveJobId(f.cad_job_id)} className="text-xs border border-[#3D6DF2] text-[#3D6DF2] hover:bg-[#3D6DF2]/10 rounded-sm font-semibold px-2 py-0.5 hover:bg-muted" data-testid={`view-cad-${f.id}`}>
                            Viewer DXF
                          </button>
                        )}
                        {f.pdf_job_id && (
                          <button onClick={() => setActivePdfJobId(f.pdf_job_id)} className="text-xs border border-[#3D6DF2] text-[#3D6DF2] hover:bg-[#3D6DF2]/10 rounded-sm font-semibold px-2 py-0.5 hover:bg-muted" data-testid={`view-pdf-${f.id}`}>
                            Viewer PDF
                          </button>
                        )}
                        {f.pdf_job_id && LEGACY_LABS && (
                          <button onClick={() => navigate(`/pdf-jobs/${f.pdf_job_id}/verify`)} className="text-xs border border-[#3D6DF2] text-[#3D6DF2] hover:bg-[#3D6DF2]/10 rounded-sm font-semibold px-2 py-0.5 hover:bg-muted" data-testid={`verify-pdf-${f.id}`}>
                            Verifica
                          </button>
                        )}
                        {f.pdf_job_id && isAdminOrOwner && LEGACY_LABS && (
                          <Link
                            to={`/pdf-jobs/${f.pdf_job_id}/diagnostics`}
                            className="text-xs border border-border px-2 py-0.5 hover:bg-muted flex items-center gap-1"
                            data-testid={`diag-pdf-${f.id}`}
                            title="Provider Diagnostics — solo Owner/Admin"
                          >
                            <BarChart3 size={10} />Diagnostics
                          </Link>
                        )}
                        <a href={`${BACKEND_URL}/api/files/${f.id}/download`} className="text-xs hover:underline" data-testid={`dl-${f.id}`}>Scarica</a>
                        <button onClick={() => deleteFile(f.id)} className="text-destructive text-xs" data-testid={`del-${f.id}`}><Trash2 size={12} /></button>
                      </div>
                    </div>
                  </div>
                ))}
                {(rfq.files || []).length === 0 && <div className="text-xs text-muted-foreground text-center py-4">Nessun file caricato.</div>}
              </div>
              <div className="mt-3 text-[11px] text-muted-foreground bg-muted p-2 border border-border">
                <b>Analisi DXF</b>: attiva · <b>Lettore PDF tecnico (beta)</b>: attiva (vector text + OCR, vision AI opzionale). STEP: non ancora implementato.
              </div>
            </div>
          </section>

          {activeJobId && <CadJobPanel jobId={activeJobId} onApproved={loadAll} />}
          {activePdfJobId && (
            <PdfAnalysisPanel
              jobId={activePdfJobId}
              rfqId={id}
              onHandoff={(evt) => { if (evt?.newJobId) setActivePdfJobId(evt.newJobId); loadAll(); }}
            />
          )}

          <section className="border border-border bg-white">
            <div className="px-4 py-3 border-b border-border font-display font-bold text-sm">Dati tecnici (revisione manuale)</div>
            <div className="px-4 pt-3 text-[11px] text-muted-foreground bg-warning/10">
              ⚠️ <b>Non compilare a mano campi già presenti nel disegno.</b> L'analisi automatica DXF/PDF/STEP dovrebbe popolare materiale, spessore, dimensioni, pieghe, fori, area. Usa questa sezione solo per correggere o completare quando l'analisi <b>non</b> è disponibile o segnala un dato realmente mancante. <br />Stato: <b>AUTOMATIC DRAWING UNDERSTANDING: IN DEVELOPMENT — NOT YET VALIDATED</b>.
            </div>
            <div className="p-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <Label>Materiale</Label>
                <Select value={inputs.material_id || ""} onValueChange={(v) => setI("material_id", v)}>
                  <SelectTrigger data-testid="mi-material"><SelectValue placeholder="Seleziona materiale" /></SelectTrigger>
                  <SelectContent>
                    {config.materials.map(m => <SelectItem key={m.id} value={m.id}>{m.name} — {m.density_kg_m3} kg/m³</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Macchina taglio</Label>
                <Select value={inputs.machine_id || ""} onValueChange={(v) => setI("machine_id", v)}>
                  <SelectTrigger data-testid="mi-machine"><SelectValue placeholder="Seleziona macchina" /></SelectTrigger>
                  <SelectContent>
                    {config.machines.filter(m => m.type !== "press_brake").map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Spessore (mm)</Label><Input data-testid="mi-thickness" type="number" step="0.1" value={inputs.thickness_mm ?? ""} onChange={(e) => setI("thickness_mm", parseFloat(e.target.value) || null)} /></div>
              <div><Label>Area netta (mm²) <span className="text-[9px] uppercase tracking-widest text-muted-foreground ml-1">se non nel disegno</span></Label><Input data-testid="mi-area" type="number" value={inputs.area_mm2 ?? ""} onChange={(e) => setI("area_mm2", parseFloat(e.target.value) || null)} /></div>
              <div><Label>Volume (mm³) — opz.</Label><Input data-testid="mi-volume" type="number" value={inputs.volume_mm3 ?? ""} onChange={(e) => setI("volume_mm3", parseFloat(e.target.value) || null)} /></div>
              <div><Label>Peso override (kg) — opz.</Label><Input data-testid="mi-weight" type="number" step="0.001" value={inputs.weight_override_kg ?? ""} onChange={(e) => setI("weight_override_kg", parseFloat(e.target.value) || null)} /></div>
              <div><Label>Lunghezza taglio (mm) <span className="text-[9px] uppercase tracking-widest text-muted-foreground ml-1">derivato da DXF</span></Label><Input data-testid="mi-cutlen" type="number" value={inputs.cut_length_mm ?? ""} onChange={(e) => setI("cut_length_mm", parseFloat(e.target.value) || null)} /></div>
              <div><Label>Sfondamenti <span className="text-[9px] uppercase tracking-widest text-muted-foreground ml-1">derivato da DXF</span></Label><Input data-testid="mi-pierces" type="number" value={inputs.pierces ?? ""} onChange={(e) => setI("pierces", parseInt(e.target.value) || 0)} /></div>
              <div><Label>Numero pieghe <span className="text-[9px] uppercase tracking-widest text-muted-foreground ml-1">se non nel disegno</span></Label><Input data-testid="mi-bends" type="number" value={inputs.bends ?? ""} onChange={(e) => setI("bends", parseInt(e.target.value) || 0)} /></div>
              <div><Label>Quantità (virgola)</Label><Input data-testid="mi-qty" value={quantities} onChange={(e) => setQuantities(e.target.value)} /></div>
              <div className="col-span-2 text-[11px] text-muted-foreground bg-muted p-2 border border-border">
                <b>Nota:</b> i campi <i>Area netta</i>, <i>Lunghezza taglio</i>, <i>Sfondamenti</i>, <i>Numero pieghe</i> sono in <b>revisione manuale</b>. In presenza di analisi automatica (DXF/PDF/STEP) verranno popolati dal sistema. <a href="#pdf-analysis" className="underline">Vai all'analisi automatica</a> per estrarli dal disegno.
              </div>
              <div className="col-span-2">
                <Button onClick={calculate} className="w-full" data-testid="btn-calculate"><Calculator size={14} className="mr-2" />Calcola preventivo</Button>
              </div>
            </div>
          </section>
        </div>

        {/* RIGHT: results */}
        <div className="space-y-4">
          {calc && <Reliability level={calc.reliability} reasons={calc.warnings || []} />}
          {calc ? (
            <>
              <section className="border border-border bg-white">
                <div className="px-4 py-3 border-b border-border font-display font-bold text-sm">Distinta costi per quantità</div>
                <table className="dense-table w-full">
                  <thead>
                    <tr><th>Q.tà</th><th>Costo ind.</th><th data-testid="rfq-price-policy-heading">{calc.lines[0]?.sale_policy?.label || "Margine"} %</th><th>Prezzo unit.</th><th>Totale</th></tr>
                  </thead>
                  <tbody>
                    {calc.lines.map(l => (
                      <tr key={l.quantity} data-testid={`calc-row-${l.quantity}`}>
                        <td className="font-mono font-bold">{l.quantity}</td>
                        <td className="font-mono text-right">{fmtNum(l.breakdown.industrial_cost)}</td>
                        <td className="font-mono text-right" data-testid={`rfq-price-policy-${l.quantity}`}>{fmtNum(l.sale_policy?.pct ?? l.breakdown.margin_pct, 1)}%</td>
                        <td className="font-mono text-right font-bold">{fmtNum(l.unit_price)}</td>
                        <td className="font-mono text-right">{fmtNum(l.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
              <section className="border border-border bg-white">
                <div className="px-4 py-3 border-b border-border font-display font-bold text-sm">Breakdown (quantità min.)</div>
                <table className="dense-table w-full">
                  <tbody>
                    {calc.lines[0] && Object.entries(calc.lines[0].breakdown).map(([k, v]) => (
                      <tr key={k}>
                        <td className="text-muted-foreground uppercase text-[10px] tracking-widest">{k.replace(/_/g, " ")}</td>
                        <td className="font-mono text-right">{typeof v === "number" ? fmtNum(v, 4) : v}</td>
                      </tr>
                    ))}
                    <tr><td className="text-muted-foreground uppercase text-[10px] tracking-widest">Peso stimato (kg)</td><td className="font-mono text-right">{fmtNum(calc.lines[0]?.weight_kg, 4)}</td></tr>
                  </tbody>
                </table>
              </section>
              <Button onClick={createQuote} disabled={saving || calc.reliability === "red"} className="w-full" data-testid="btn-create-quote">
                {calc.reliability === "red" ? "Impossibile creare (rosso)" : (saving ? "Salvataggio…" : "Crea preventivo formale")}
              </Button>
            </>
          ) : (
            <div className="border border-dashed border-border p-12 text-center text-muted-foreground text-sm">
              <Calculator size={32} className="mx-auto mb-2" />
              Compila i dati manuali e premi <b>Calcola preventivo</b>.
              <div className="text-xs mt-3">Il motore è deterministico: nessuna AI stima il prezzo.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
